import Styled, { css } from 'styled-components';
import merge from 'lodash/merge';

import { cssDeclaration, VALID_DIMENSION_DATA } from '../Utils';
import IThemeInterface from '../Theme/common';
import { FONT_MAP } from '../../../lib/StyledComponents/StyledComponents';

import {
  IDescriptionGroupContext,
  useDescriptionGroupContext,
  useDescriptionListContext
} from './DescriptionListContext';
import { IIDescriptionContentTheme } from './DescriptionGroup';

const WIDTH_STACKED_ON_MOBILE = { base: 'auto' };
const PADDING_NOT_STACKED_ON_MOBILE = { base: { right: '0' }};

const getWidth = (widthProp: VALID_DIMENSION_DATA, noMobileStack?: boolean) => {
  if (typeof widthProp === 'object') {
    return noMobileStack ? widthProp : merge(widthProp, WIDTH_STACKED_ON_MOBILE);
  } else {
    return noMobileStack ? widthProp : {...WIDTH_STACKED_ON_MOBILE, small: widthProp};
  }
};

const getDescriptionItemStyles = (themeObj: IIDescriptionContentTheme, themeTwo: IThemeInterface) => (
  css`
    font-weight: ${themeObj.weight && themeObj.weight};
    font-family: ${themeObj.family && FONT_MAP[themeObj.family]};

    /* color */ ${themeObj.color && cssDeclaration(themeTwo, 'color', themeObj.color)};
    /* font-size */ ${themeObj.size && cssDeclaration(themeTwo, 'font-size', themeObj.size)};
    /* line-height */ ${themeObj.lineHeight && cssDeclaration(themeTwo, 'line-height', themeObj.lineHeight)};
  `
);

export const DescriptionTerm = Styled.dt<IDescriptionGroupContext>`
  word-break: break-all;

  ${({ theme: { themeTwo } }) => {
    const { descriptionList: { term: termTheme, variant: variantTheme }} = themeTwo;
    const { term, variant } = useDescriptionGroupContext();
    const {
      fixTermWidth,
      stacked,
      align,
      noMobileStack,
      contentOrderSwitch,
    } = useDescriptionListContext();
    const width = fixTermWidth && getWidth(fixTermWidth, noMobileStack);
    // removes padding on noMobileStack dt
    const margin = noMobileStack ? termTheme.margin : {...PADDING_NOT_STACKED_ON_MOBILE, small: termTheme.margin};
    const themedVariant = variant && variantTheme[variant];

    return css`
      // theme styles
      ${getDescriptionItemStyles(termTheme, themeTwo)};

      ${fixTermWidth && css`
        flex-shrink: 0;
        /* width */ ${width && cssDeclaration(themeTwo, 'width', width)}
      `}

      ${(!fixTermWidth && !stacked && !align) && css`
        /* margin */ ${cssDeclaration(themeTwo, 'margin', margin)};
      `}

      ${(contentOrderSwitch && stacked) && css`
        order: 2;
      `}

      // variant theme
      ${themedVariant && getDescriptionItemStyles(themedVariant.term, themeTwo)};

      // these values override the theme/variants; must stay below theme values
      ${term && getDescriptionItemStyles(term, themeTwo)};
    `;
  }}
`;

export const DescriptionDetail = Styled.dd<IDescriptionGroupContext>`
  word-break: break-all;

  ${({ theme: { themeTwo }}) => {
    const { descriptionList: { detail: detailTheme, variant: variantTheme }} = themeTwo;
    const { fixTermWidth } = useDescriptionListContext();
    const { detail, variant } = useDescriptionGroupContext();
    const themedVariant = variant && variantTheme[variant];

    return css`
      // theme styles
      ${getDescriptionItemStyles(detailTheme, themeTwo)};

      ${fixTermWidth && 'flex-grow: 1;'}

      // variant theme
      ${themedVariant && getDescriptionItemStyles(themedVariant.detail, themeTwo)};

      // these values override the theme/variants; must stay below theme values
      ${detail && getDescriptionItemStyles(detail, themeTwo)};
    `;
  }}
`;
